import { TENANTS } from '@/config/tenants';

// Platform is set at build time via VITE_PLATFORM env var: 'web' | 'ios' | 'android'
// This avoids importing @capacitor/core on web builds entirely.
// When building for mobile, set VITE_PLATFORM=ios or VITE_PLATFORM=android
// so Rollup inlines the value and tree-shakes all web paths.
const _platform = (import.meta.env.VITE_PLATFORM as string) ||
  (typeof window !== 'undefined' && (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor?.getPlatform?.()) ||
  'web';

export const platform = _platform;
export const isNativePlatform = _platform === 'ios' || _platform === 'android';
export const isIOS = _platform === 'ios';
export const isAndroid = _platform === 'android';
export const isWeb = _platform === 'web';

/**
 * Returns the public app origin for use in shareable URLs.
 * On native (Capacitor), window.location.origin is `https://localhost` — use the env var instead.
 * Falls back to window.location.origin on web (dev server, Amplify, etc.)
 */
export const appOrigin = (import.meta.env.VITE_APP_ORIGIN as string | undefined)
  ?? window.location.origin;

/**
 * Initialize Capacitor plugins and configure mobile-specific behavior.
 * Plugin packages are dynamically imported so they are excluded from the web bundle.
 */
export async function initializeCapacitor() {
  // Only run on native platforms
  if (!isNativePlatform) {
    return;
  }

  console.log(`Initializing Capacitor on ${platform}`);

  try {
    const [
      { StatusBar, Style },
      { SplashScreen },
      { App },
      { Keyboard },
    ] = await Promise.all([
      import('@capacitor/status-bar'),
      import('@capacitor/splash-screen'),
      import('@capacitor/app'),
      import('@capacitor/keyboard'),
    ]);

    // On Android, use edge-to-edge support plugin to inset the WebView
    // below the status bar and above the nav bar, then paint the status
    // bar area with the header-navy color so it blends with the header.
    if (isAndroid) {
      const { EdgeToEdge } = await import('@capawesome/capacitor-android-edge-to-edge-support');
      await EdgeToEdge.enable();
      await EdgeToEdge.setStatusBarColor({ color: '#111e30' });
      await EdgeToEdge.setNavigationBarColor({ color: '#ffffff' });
    }

    // Configure status bar icon style (light icons for dark background)
    await StatusBar.setStyle({ style: Style.Dark });

    // Hide splash screen after app is ready
    await SplashScreen.hide();

    // Handle Android back button
    App.addListener('backButton', ({ canGoBack }) => {
      if (!canGoBack) {
        App.exitApp();
      } else {
        window.history.back();
      }
    });

    // Handle app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active:', isActive);
    });

    // Handle deep links — routes magic-link URLs back into the WebView
    App.addListener('appUrlOpen', (data) => {
      const url = new URL(data.url);
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      const allowedHosts = new Set<string>();
      for (const tenant of Object.values(TENANTS)) {
        for (const d of tenant.domains) {
          allowedHosts.add(d.toLowerCase().replace(/^www\./, ''));
        }
      }
      try {
        allowedHosts.add(
          new URL(appOrigin).hostname.toLowerCase().replace(/^www\./, '')
        );
      } catch {
        /* ignore invalid VITE_APP_ORIGIN */
      }
      if (!allowedHosts.has(host)) return;
      const path = url.pathname + url.search;
      window.location.href = path;
    });

    // Keyboard events
    Keyboard.addListener('keyboardWillShow', (info) => {
      console.log('Keyboard will show with height:', info.keyboardHeight);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      console.log('Keyboard will hide');
    });

    console.log('Capacitor initialized successfully');
  } catch (error) {
    console.error('Error initializing Capacitor:', error);
  }
}

/**
 * Cleanup Capacitor listeners on app unmount
 */
export async function cleanupCapacitor() {
  if (!isNativePlatform) return;

  try {
    const [{ App }, { Keyboard }] = await Promise.all([
      import('@capacitor/app'),
      import('@capacitor/keyboard'),
    ]);
    await App.removeAllListeners();
    await Keyboard.removeAllListeners();
  } catch (error) {
    console.error('Error cleaning up Capacitor:', error);
  }
}
